import React from "react";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { GetCounterpartiesQuery } from "@common/schemas/counterparty.schema";
import { TaskType } from "@common/types/task";
import {
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { CloseTemplateCounterpartyPreviewHeader } from "./CloseTemplateCounterpartyPreviewHeader";
import { PreviewEntityScope } from "./dataPreview/previewEntityScope";
import { TemplateCounterpartiesDataTable } from "./dataPreview/TemplateCounterpartiesDataTable";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import { useCloseTemplateTaskViewState } from "./useCloseTemplateTaskViewState";

interface Props {
    taskType: TaskType;
    viewConfig: CloseTemplateViewConfig | null;
    onViewConfigChange: (viewConfig: CloseTemplateViewConfig) => void;
    parseViewConfig: (viewConfig: object | null) => CounterpartyDirectoryView;
    serializeView: (view: CounterpartyDirectoryView) => CloseTemplateViewConfig;
    normalizeView?: (
        view: CounterpartyDirectoryView,
    ) => CounterpartyDirectoryView;
    createDefaultView: () => CounterpartyDirectoryView;
    availableFilters: FilterTabs[];
    headerActions?: React.ReactNode;
    extraQueryFilters?: Partial<GetCounterpartiesQuery>;
    entityScope?: PreviewEntityScope;
}

export const CloseTemplateCounterpartyDataPreview: React.FC<Props> = ({
    taskType,
    viewConfig,
    onViewConfigChange,
    parseViewConfig,
    serializeView,
    normalizeView,
    createDefaultView,
    availableFilters,
    headerActions,
    extraQueryFilters,
    entityScope,
}) => {
    const { view, handleViewChange, handleFiltersChange } =
        useCloseTemplateTaskViewState({
            viewConfig,
            onViewConfigChange,
            parseViewConfig,
            serializeView,
            viewSchema: CounterpartyDirectoryViewSchema,
            normalizeView,
        });
    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType,
        viewConfig: viewConfig ?? serializeView(view),
        entityScope,
    });

    return (
        <div>
            <CloseTemplateCounterpartyPreviewHeader
                view={view}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                createDefaultView={createDefaultView}
            >
                {headerActions}
                <TemplatePreviewSwitcher target={previewTarget} />
            </CloseTemplateCounterpartyPreviewHeader>

            <TemplateCounterpartiesDataTable
                templateView={view}
                onTemplateViewChange={handleViewChange}
                resolvedView={resolvedView}
                workspaceId={previewTarget.workspaceId}
                extraQueryFilters={extraQueryFilters}
            />
        </div>
    );
};
